import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global axios defaults - disable caching
axios.defaults.headers.common['Cache-Control'] = 'no-cache, no-store, must-revalidate';
axios.defaults.headers.common['Pragma'] = 'no-cache';
axios.defaults.headers.common['Expires'] = '0';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [accountDeactivated, setAccountDeactivated] = useState(null);
  const sessionCheckInterval = useRef(null);

  // Create api instance that updates with token
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    
    if (token) {
      instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    return instance;
  }, [token]);

  // Session status check for active session invalidation
  const checkSessionStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await axios.get(`${API}/auth/session-status`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000 // 10 second timeout
      });
      
      if (!res.data.valid) {
        // Session invalidated - force logout
        if (res.data.reason === 'account_deactivated') {
          setAccountDeactivated({
            message: res.data.message || 'Your account has been deactivated',
            reason: 'account_deactivated'
          });
          toast.error('Your account has been deactivated by admin');
        }
        logout();
      }
    } catch (err) {
      // Only logout on explicit 401 Unauthorized
      // Ignore network errors, CORS errors, timeouts to prevent accidental logouts
      if (err.response?.status === 401) {
        console.log('Session check: Token invalid, logging out');
        logout();
      } else if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' || !err.response) {
        // Network error, CORS error, or timeout - don't logout, just log
        console.warn('Session check network error (not logging out):', err.message);
      } else {
        console.warn('Session check error:', err.response?.status, err.message);
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUser();
      
      // Start session status check interval (every 30 seconds)
      sessionCheckInterval.current = setInterval(checkSessionStatus, 30000);
    } else {
      setLoading(false);
    }
    
    return () => {
      if (sessionCheckInterval.current) {
        clearInterval(sessionCheckInterval.current);
      }
    };
  }, [token, checkSessionStatus]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000 // 15 second timeout
      });
      setUser(res.data);
      setAccountDeactivated(null);
    } catch (err) {
      console.error('Failed to fetch user:', err);
      // Only logout on explicit 401 - not on network/CORS errors
      if (err.response?.status === 401) {
        console.log('fetchUser: Token invalid (401), logging out');
        logout();
      } else if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' || !err.response) {
        console.warn('fetchUser: Network error, keeping session:', err.message);
        // Don't logout on network errors - user might just have connectivity issues
      } else {
        console.warn('fetchUser: Other error:', err.response?.status);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    console.log('Attempting login for:', email);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, {
        timeout: 15000,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log('Login response:', res.data);
      const { access_token, user: userData } = res.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      setAccountDeactivated(null);
      return userData;
    } catch (err) {
      // Enhanced error handling for CORS and network issues
      if (err.code === 'ERR_NETWORK' || !err.response) {
        console.error('Login network/CORS error:', err);
        throw new Error('Network error. Please check your connection and try again.');
      }
      throw err;
    }
  };

  const register = async (data) => {
    console.log('Attempting registration for:', data.email);
    const res = await axios.post(`${API}/auth/register`, data);
    console.log('Register response:', res.data);
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setAccountDeactivated(null);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.setItem('wallet_auto_connect', 'false'); // Prevent auto-connect on next visit
    setToken(null);
    setUser(null);
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
    }
  };

  // Direct auth setter (for wallet login or when we already have token/user)
  const setAuth = (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('wallet_auto_connect', 'true'); // Re-enable auto-connect after successful login
    setToken(accessToken);
    setUser(userData);
    setAccountDeactivated(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(res.data);
      } catch (err) {
        console.error('Failed to refresh user:', err);
      }
    }
  };
  
  // Check account status before wallet login
  const checkAccountStatus = async (walletAddress) => {
    try {
      const res = await axios.get(`${API}/auth/check-account-status?wallet_address=${walletAddress}`);
      return res.data;
    } catch (err) {
      console.error('Failed to check account status:', err);
      return { exists: false, is_active: true, message: null };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    setAuth,
    refreshUser,
    checkAccountStatus,
    accountDeactivated,
    setAccountDeactivated,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    api
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
